import React from 'react'
import { createRoot } from 'react-dom/client'

import EmbeddedApp from './embedded_app'

import {
  getUserInfo,
  isEdge,
  isMobile,
  mergeDeep,
  subscribeToPushEvents,
  getAPIEndpoint,
  getChannelParams,
  getQueryParams,
} from '../common/util'
import { determineUILocale } from '../common/labels'

import hostCss from './host.scss?inline'

const MUTED = '_botsqd_muted'

class BotSquad {
  constructor(config) {
    this._settings = {
      locale: null,
      playSound: true,
      debug: false,
      notificationManager: true,
      id: null,
      user_id: null,
      context: {},
      socket: null,
      css_variables: {},
      hide_input: false,
      height: '450px',
      width: '350px',
      layout: 'minimized',
      container_id: null,
      socket_params: {},
      alwaysFocus: !isMobile(),
      muted: MUTED in localStorage,
      chat_config: { disabled_inputs: [] },
      ui_labels: {},
    }

    this._callbacks = {}
    this._emitCallbacks = {}

    // script loaded after DOM completed
    if (/comp|inter|loaded/.test(document.readyState)) {
      this._init(config)
    } else {
      document.addEventListener(
        'DOMContentLoaded',
        () => {
          this._init(config)
        },
        false,
      )
    }
  }

  // ##########################################################################
  // ##                              PUBLIC                                 ##
  // ##########################################################################

  config(key, val, persist = true) {
    if (this._settings[key] === undefined) {
      console.warn(`BotSqd: Unknown config item ${key}`)
      return false
    }
    if (val === undefined) {
      return this._settings[key]
    } else {
      const oldValue = this._settings[key]
      this._settings[key] = val
      if (persist) {
        this._setSetting(key, oldValue)
        this._persistSettings()
      }
      return oldValue
    }
  }

  message(text) {
    this._postFrame('message', text)
  }

  media(type, url) {
    this._postFrame('media', { type, url })
  }

  location(lat, lon) {
    this._postFrame('location', { lat, lon })
  }

  event(name, payload) {
    this._postFrame('event', { name, payload: payload || {} })
  }

  on(event, callback) {
    this._callbacks[event] = this._callbacks[event] || []
    this._callbacks[event].push(callback)
  }

  off(event, callback) {
    if (this._callbacks[event]) {
      this._callbacks[event] = this._callbacks[event].filter(cb => cb !== callback)
    }
  }

  onEmit(event, callback) {
    this._emitCallbacks[event] = this._emitCallbacks[event] || []
    this._emitCallbacks[event].push(callback)
  }

  offEmit(event, callback) {
    if (this._emitCallbacks[event]) {
      this._emitCallbacks[event] = this._emitCallbacks[event].filter(cb => cb !== callback)
    }
  }

  getLastEmitted(eventName, success, fail = null) {
    const event = this._postFrame('getLastEmitted', eventName)
    if (event !== undefined) {
      success(JSON.parse(event.payload.json))
    } else {
      typeof fail === 'function' && fail()
    }
  }

  visitorEvent(_name, _payload) {}

  // ##########################################################################
  // ##                              PRIVATE                                 ##
  // ##########################################################################

  _init(config) {
    const style = document.createElement('style')
    style.setAttribute('v', 'botsquad')
    style.textContent = hostCss
    document.head.appendChild(style)

    // Setting the config for each option
    const opt = this._settings
    Object.keys(config).forEach(key => {
      opt[key] = config[key]
    })
    if (config.instance) {
      // initialize from localStorage
      this._loadFromSavedSettings(config.instance)
    }

    Object.keys(opt)
      .sort()
      .forEach(key => {
        this.config(key, opt[key], false)
      })

    if (this._settings.layout === 'minimized') {
      this.config('css_variables', this._settings.css_variables)
      this._ensureButton()
    }
    if (this._settings.layout !== 'minimized' && this._settings.layout !== 'hidden') {
      this._ensureFrame()
    }

    return true
  }

  _setupDefaultEmits() {
    const mapping = {
      hide_input: flag => this.config('hide_input', flag),
    }

    Object.keys(mapping).forEach(emit => {
      const callback = mapping[emit]
      this.onEmit(emit, callback)
      this.getLastEmitted(emit, callback)
    })
  }

  _setCSSVariable(key, value) {
    document.documentElement.style.setProperty(`--botsquad-${key.replace(/_/g, '-')}`, value)
  }

  _maybeSaveSetting(key, value) {
    if (this._settings.instance) {
      const k = `_botsqd.${this._settings.instance}.${key}`
      localStorage[k] = value
    }
  }

  _loadFromSavedSettings(instance) {
    const prefix = `_botsqd.${instance}.`
    Object.keys(localStorage)
      .filter(k => k.startsWith(prefix))
      .forEach(k => {
        this._settings[k.replace(prefix, '')] = localStorage[k]
      })
  }

  _setSetting(key, oldValue) {
    const value = this._settings[key]
    switch (key) {
      case 'container_id':
        if (!this._frame) {
          this._ensureFrame()
        }
        break
      case 'height':
        this._frame.style.height = value
        break
      case 'width':
        this._frame.style.width = value
        break
      case 'css_variables':
        Object.keys(value).forEach(key => {
          this._setCSSVariable(key, value[key])
        })
        break
      case 'layout':
        this._maybeSaveSetting(key, value)

        if (oldValue && value !== oldValue && value === 'docked') {
          this._triggerCallback('event', 'open')
        }

        switch (value) {
          case 'embedded':
          case 'contained':
            if (!this._settings.container_id) {
              console.warn('Setting embedded / contained mode failed, no container_id given.')
              break
            }
            this._ensureFrame(() => this._toggleFrame(oldValue))
            break
          case 'docked':
            this._ensureButton(true)
            this._button.classList.toggle('loading', true)
            this._ensureFrame(() => this._toggleFrame(oldValue))
            break
          case 'minimized':
            this._ensureButton()
            this._toggleFrame(oldValue)
            this._button.classList.remove('hidden')
            break
          case 'hidden':
            this._toggleFrame(oldValue)
            this._button && this._button.classList.add('hidden')
            break
        }
        break
    }
  }

  _persistSettings() {
    this._postFrame('settings', this._settings)
  }

  _triggerCallback(type, event, data) {
    let callbacks
    if (type === 'emit') {
      callbacks = this._emitCallbacks[event]
    } else if (type === 'event') {
      callbacks = this._callbacks[event]
    }

    if (callbacks) {
      callbacks.forEach(callback => {
        callback(data)
      })
    }
  }

  _onReady = () => {
    if (this._loaded) {
      return
    }
    this._loaded = true

    // Persist our setting to the frame
    this._persistSettings()
    // Set our initial settings
    Object.keys(this._settings).forEach(key => {
      this._setSetting(key)
    })

    this._frame.classList.toggle('loading', false)
    if (this._button) this._button.classList.toggle('loading', false)
    this._setupDefaultEmits()

    this._triggerCallback('event', 'loaded')

    if (this._settings.layout === 'embedded') {
      setTimeout(() => this.scrollToBottom(), 50)
    }

    if (this._frameContinuation) {
      this._frameContinuation()
      this._frameContinuation = null
    }
  }

  close() {
    this.config('layout', this._settings.container_id ? 'embedded' : 'minimized')
    this._triggerCallback('event', 'close')
  }

  _appQueue = []
  _setApp(app) {
    this._app = app
    if (this._app) {
      this._appQueue.forEach(({ name, data }) => this._app.handleMessage(name, data))
      this._appQueue = []
    }
  }

  _postFrame(name, data = null) {
    if (this._app) {
      return this._app.handleMessage(name, data)
    } else {
      this._appQueue.push({ name, data })
    }
  }

  _log() {
    if (this._settings.debug === true) {
      console.log(...arguments)
    }
  }

  scrollToBottom() {
    const f = this._frame.getBoundingClientRect()
    const b = document.documentElement.getBoundingClientRect()
    const t = Math.max(0, f.top - b.top + this._frame.clientHeight - window.innerHeight)
    //window.scrollTo(0,isMobile() ? document.body.scrollHeight : t + 50)
    window.scrollTo(0, t)
  }

  _validateSettings() {
    const { id } = this._settings
    if (!id || !id.length) {
      throw new Error("BotSquad widget: required 'id' parameter missing'")
    }
  }

  _ensureButton(hidden) {
    if (this._button) {
      return
    }

    // Inject button
    this._button = document.createElement('button')
    this._button.id = 'botsquad-button'
    this._button.addEventListener('click', () => {
      this.config('layout', 'docked')
    })

    this._button.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 79.8 79.8" id="svg10"><g id="Page-1" transform="translate(-218.502 82.732)" fill="none" fill-rule="evenodd"><circle id="circle" cx="258.4" cy="-42.8" r="39.9" fill="#00f" stroke-width="1.1"/><path id="SQUAD-3" d="M270-22s-1.5.7-5.2 1H252c-4.7-.3-8.4-1.7-11.2-4.2-3-2.8-4.7-7-4.8-12.3v-10.3c0-5.3 1.8-9.4 5-12.2 2.7-2.6 6.5-4 11-4.2h12.7c4.8.2 8.6 1.6 11.5 4.2 3 2.8 4.8 7 5 12.2v10.2c-.2 5.4-1.7 9.3-4.4 12l6.8 10.4s-12-6-13.7-7zm-20.5-7.7c-1.7 0-3-1.3-3-3 0-1.6 1.3-3 3-3h18.7c1.7 0 3 1.4 3 3 0 1.7-1.3 3-3 3zm0-20c-1.7 0-3-1.3-3-3 0-1.6 1.3-3 3-3h18.7c1.7 0 3 1.4 3 3 0 1.7-1.3 3-3 3zm-4.6 10c-2 0-3-1.3-3-3 0-1.6 1-3 3-3h27c1.3 0 3 1.4 3 3 0 1.7-1.7 3-3 3z" fill="#fff"/></g></svg>`

    if (hidden) {
      this._button.classList.add('hidden')
    }

    document.body.appendChild(this._button)
  }

  _hostCheck({ domains }) {
    const host = document.location.hostname
    if (
      host === 'localhost' ||
      host.endsWith('.localhost') ||
      host === '127.0.0.1' ||
      host == 'bsqd.me' ||
      host.endsWith('.bsqd.me')
    ) {
      return true
    }
    if (!domains) return true
    if (domains.indexOf(host) >= 0) {
      return true
    }
    alert(`This domain, ${host}, is not allowed to host this chatbot.`)
    return false
  }

  subscribeToPushEvents(pushManager, applicationServerKey) {
    const { user_id } = getChannelParams(this._settings.user_id)
    return subscribeToPushEvents(
      this._settings.socket || 'wss://bsqd.me/socket',
      this._settings.id,
      user_id,
      pushManager,
      applicationServerKey,
    )
  }

  _ensureFrame(continuation) {
    if (this._frame) {
      continuation && continuation()
      return
    }
    this._validateSettings()

    this._frameContinuation = continuation
    this._frame = document.createElement('div')
    this._frame.setAttribute('id', 'botsquad-frame')
    this._frame.className = 'layout-docked loading'
    const element = this._settings.container_id ? document.getElementById(this._settings.container_id) : document.body
    element.appendChild(this._frame)

    fetch(getAPIEndpoint(this._settings.socket || 'wss://bsqd.me/socket', this._settings.id)).then(response => {
      response.json().then(bot => {
        const locale = this._settings.locale || determineUILocale(null, bot)

        const user = getUserInfo()
        user.extra = { ...user.extra, ip: response.headers.get('x-client-ip') }
        const query = getQueryParams()

        const context = mergeDeep({ user, query }, this._settings.context)
        const connect_payload = { origin: document.location.href }
        const params = {
          ...getChannelParams(this._settings.user_id),
          connect_payload,
          context,
          initial_locale: locale,
          ...this._settings.socket_params,
        }
        const userAvatar = params.context && params.context.user ? params.context.user.profile_picture : null

        const props = {
          bot_id: this._settings.id,
          notificationManager: this._settings.notificationManager,
          bot,
          socket: this._settings.socket || `wss://bsqd.me/socket`,
          botAvatar: bot.profile_picture,
          userAvatar, // userAvatar || document.querySelector("link[rel=default-user-avatar]").href,
          params,
          settings: this._settings,
        }
        this._root = createRoot(this._frame)
        this._root.render(<EmbeddedApp {...props} host={this} ref={app => this._setApp(app)} />)
      })
    })
  }

  _toggleFrame() {
    if ((this._settings.layout === 'hidden' || this._settings.layout === 'minimized') && !this._frame) {
      return
    }

    this._frame.classList.toggle('mobile', isMobile())

    const layouts = ['embedded', 'contained', 'docked', 'minimized', 'hidden']
    for (const layout of layouts) {
      this._frame.classList.toggle('layout-' + layout, this._settings.layout === layout)
      document.body.classList.toggle('botsquad-layout-' + layout, this._settings.layout === layout)
      document.body.classList.toggle('mobile', isMobile())
      document.body.classList.toggle('botsquad-ms-edge', isEdge())
    }

    if (this._settings.layout === 'embedded' || this._settings.layout === 'contained') {
      this._frame.style.height = ''
      this._frame.style.width = ''
      if (this.button) {
        this._button.classList.add('hidden')
        this._button.classList.remove('loading')
      }
    }
    if (this._settings.layout === 'docked') {
      this._frame.style.width = this._settings.width
      this._frame.style.height = this._settings.height
      if (this._button) {
        this._button.classList.add('hidden')
        this._button.classList.remove('loading')
      }
      setTimeout(() => {
        this._postFrame('focus', true)
      }, 0)
    }
  }

  isMuted() {
    return this._settings.muted
  }

  setMuted(flag) {
    this._settings.muted = flag
    if (flag) {
      localStorage[MUTED] = 'true'
    } else {
      delete localStorage[MUTED]
    }
  }

  mayPlaySound() {
    return !this.isMuted() && this._settings.playSound
  }

  isChatVisible() {
    return (
      this._settings.layout === 'docked' ||
      this._settings.layout === 'contained' ||
      this._settings.layout === 'embedded'
    )
  }
}

const SCRIPT = document.querySelector('script[data-botsquad]')
if (SCRIPT !== null) {
  // Init and expose the default BotSqd
  window.BotSqd = new BotSquad(JSON.parse(SCRIPT.dataset.botsquad))
}

window.BotSquadHost = BotSquad
